import { SearchSelect } from '@/components/search-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { StaffFilter } from '@/components/staff-filter'
import { ProjectPriorityFilter } from '@/components/project-priority-filter'
import { ServiceTierFilter } from '@/components/service-tier'
import { ListFilter } from '@/components/list-filters'
import type { ProjectFilterValues } from '@/lib/project-filters'
import { stageLabel } from '@/features/home/model'

// The words on each project's status pill, which come from its POs (USA-1451).
const STAGE_OPTIONS = [
  'ordering',
  'po_review',
  'processing',
  'backordered',
  'on_hold',
  'needs_attention',
  'part_shipped',
  'in_transit',
  'part_delivered',
  'delivered',
  'cancelled',
]

/**
 * The Projects table's filters, each in a `ListFilter` for `ListFilters`: the Projects page's and a
 * manufacturer's Projects tab (USA-1740). While the header's My Projects is on (`staffLocked`), the
 * Staff filter shows its person (`lockedStaffId`) and cannot change.
 */
export function ProjectFilterFields({
  values,
  onChange,
  staffLocked = false,
  lockedStaffId,
}: {
  values: ProjectFilterValues
  onChange: (change: Partial<ProjectFilterValues>) => void
  staffLocked?: boolean
  lockedStaffId?: number
}) {
  return (
    <>
      <ListFilter label="Status">
        <SearchSelect
          ariaLabel="Project status filter"
          className="h-9 w-full"
          value={values.stage ?? values.status}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'all', label: 'All Statuses' },
            ...STAGE_OPTIONS.map((key) => ({ value: key, label: stageLabel(key) })),
          ]}
          onChange={(v) => {
            if (!v) return
            // A pill word filters every project; Active is the open ones, as before.
            onChange(STAGE_OPTIONS.includes(v) ? { stage: v, status: 'all' } : { status: v, stage: null })
          }}
          placeholder="Status"
          searchPlaceholder="Search statuses"
        />
      </ListFilter>
      <ListFilter label="Automation">
        <Select value={values.automation} onValueChange={(v) => onChange({ automation: v as ProjectFilterValues['automation'] })}>
          <SelectTrigger aria-label="Project automation filter" className="w-full">
            <SelectValue placeholder="Automation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Automation On</SelectItem>
            <SelectItem value="disabled">Automation Off</SelectItem>
            <SelectItem value="all">All Projects</SelectItem>
          </SelectContent>
        </Select>
      </ListFilter>
      <ListFilter label="Source">
        <Select value={values.source} onValueChange={(v) => onChange({ source: v as ProjectFilterValues['source'] })}>
          <SelectTrigger aria-label="Project source filter" className="w-full">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="shopify">Shopify</SelectItem>
            <SelectItem value="dropship">Internal</SelectItem>
          </SelectContent>
        </Select>
      </ListFilter>
      <ListFilter label="Priority">
        <ProjectPriorityFilter value={values.priority} onValueChange={(priority) => onChange({ priority })} />
      </ListFilter>
      <ListFilter label="Service tier">
        <ServiceTierFilter value={values.tier} onValueChange={(tier) => onChange({ tier })} />
      </ListFilter>
      <ListFilter label="Staff">
        <StaffFilter
          value={staffLocked ? lockedStaffId : (values.staff ?? undefined)}
          disabled={staffLocked}
          onValueChange={(value) => onChange({ staff: value ?? null })}
        />
      </ListFilter>
    </>
  )
}
